import { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    Button,
    Divider
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getLogKilns, postLogKiln } from '../../../../../../actions/rof-burns/rof-actions';
import KilnList from './log-kiln/KilnList';
import RofCard from './log-kiln/RofCard';
import UmptopiaCard from './log-kiln/UmptopiaCard';
import CbCard from './log-kiln/CbCard';

const FLAME_CAP_KILNS = [
    'Ring of Fire',
    'Umptopia',
];

const CONSERVATION_BURNS_KILNS = [
    'Conservation Burn - Hand Pile',
    'Conservation Burn - Machine Pile',
];

/**
 * 
 * @param {'Flame Cap Kiln' | 'Conservation Burns'} burnType 
 * @returns 
 */
const generateFormState = (burnType) => {
    const kilnType = burnType === 'Flame Cap Kiln' ? FLAME_CAP_KILNS[0] : CONSERVATION_BURNS_KILNS[0]
    return {
        kilnType: kilnType,
        kilnId: null,
        // kiln_picture,
        units: null,
        diameter: null,
        heightOfInnerPanels: null,
        numberOfPanels: null,
        heatShieldPanelWidth: null,
        heatShieldPanelLength: null,
        innerWidth: null,
        innerLength: null,
        innerHeight: null,
        height: null
    }
}

/**
 * Shared component between Flame Cap Kilns and Conservation Burns
 * @param {Object} props 
 * @param {'Flame Cap Kiln' | 'Conservation Burns'} props.burnType Prop drilled down from Programs.js
 * @returns {JSX.Element} Kiln Cards and Kiln Table
 */

export default function LogKiln({ burnType }) {
    const defaultFormState = generateFormState(burnType)
    const dispatch = useDispatch();
    const [formErrors, setFormErrors] = useState([])
    const [activeUmptopiaKiln, setActiveUmptopiaKiln] = useState(null)
    const [kilnDetails, setKilnDetails] = useState({ ...defaultFormState });
    const { selectedProject } = useSelector(state => state.postFormData);
    let { id, measureType } = selectedProject || {} //measureType
    const projectKey = id
    // const kilnDimensions = useSelector(state => state.postFormData.kilnDimensions);
    // const loading = useSelector(state => state.postFormData.loading);

    useEffect(() => {
        dispatch(getLogKilns(projectKey, kilnDetails.kilnType))
    }, [dispatch, projectKey, kilnDetails.kilnType]);

    useEffect(() => {
        setKilnDetails({ ...defaultFormState })
    }, [projectKey])

    const setData = e => {
        const { name, value } = e.target;
        setKilnDetails(prevState => ({
            ...prevState,
            [name]: value,
        }));
    }

    const onSubmit = () => {
        const updatedKilnDetails = { ...kilnDetails };
        if (updatedKilnDetails.kilnType === 'Umptopia') {
            // do nothing
        }
        else if (updatedKilnDetails.kilnType === 'Ring of Fire') {
            delete updatedKilnDetails['innerWidth'];
            delete updatedKilnDetails['innerLength'];
            delete updatedKilnDetails['innerHeight'];
            delete updatedKilnDetails['height'];
        }
        else if (
            updatedKilnDetails.kilnType === 'Conservation Burn - Hand Pile' ||
            updatedKilnDetails.kilnType === 'Conservation Burn - Machine Pile') {
            delete updatedKilnDetails['innerWidth'];
            delete updatedKilnDetails['innerLength'];
            delete updatedKilnDetails['innerHeight'];
            delete updatedKilnDetails['heightOfInnerPanels'];
            delete updatedKilnDetails['numberOfPanels'];
            delete updatedKilnDetails['heatShieldPanelWidth'];
            delete updatedKilnDetails['heatShieldPanelLength'];
        }
        else {
            delete updatedKilnDetails['diameter'];
            delete updatedKilnDetails['heightOfInnerPanels'];
            delete updatedKilnDetails['numberOfPanels'];
            delete updatedKilnDetails['heatShieldPanelWidth'];
            delete updatedKilnDetails['heatShieldPanelLength'];
        }
        for (let key in updatedKilnDetails) {
            if (updatedKilnDetails[key] === null) {
                setFormErrors([...formErrors, 'Please complete all form fields when submitting a new Kiln']);
                return;
            }
        }
        dispatch(postLogKiln(updatedKilnDetails, projectKey))
            .then(() => {
                dispatch(getLogKilns(projectKey, kilnDetails.kilnType)).then(() => {
                    handleReset();
                })
            });
    };

    const handleReset = () => {
        let resetState = {
            ...kilnDetails,
            // kilnType: 'Ring of Fire',
            kilnId: null,
            units: null,
            diameter: null,
            heightOfInnerPanels: null,
            numberOfPanels: null,
            heatShieldPanelWidth: null,
            heatShieldPanelLength: null,
            innerWidth: null,
            innerLength: null,
            innerHeight: null,
            height: null
        }
        setKilnDetails({ ...resetState });
        setFormErrors([]);
    };

    const filterKilns = () => {
        if (burnType === 'Flame Cap Kiln') {
            if (measureType === 'Metric') {
                const EXCLUDEDTYPES = ['Umptopia']
                return FLAME_CAP_KILNS.filter((kilnType) => (!EXCLUDEDTYPES.includes(kilnType)))
            }
            else return FLAME_CAP_KILNS
        }
        else return CONSERVATION_BURNS_KILNS
    }

    const kilns = filterKilns()
    const kilnTitle = {
        'Conservation Burn - Hand Pile': 'Conservation Burn - Hand Pile',
        'Conservation Burn - Machine Pile': 'Conservation Burn - Machine Pile',
        'Ring of Fire': 'Ring of Fire Kiln',
        'Umptopia': 'Umptopia Kiln',
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex' }}>
                <Typography variant='h4' sx={{ mb: 2 }}>
                    {kilnTitle[kilnDetails.kilnType]}
                </Typography>
            </Box>
            <FormControl
                fullWidth
            >
                <InputLabel sx={{ background: 'white' }}>
                    Select type of kiln
                </InputLabel>
                <Select name='kilnType' value={kilnDetails.kilnType} onChange={setData}>
                    {kilns.map((kiln, index) => (
                        <MenuItem key={index} value={kiln}>
                            {kiln}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {kilnDetails.kilnType === 'Ring of Fire' &&
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant='h5'> Select the number of Kiln Panels:</Typography>
                    <RofCard setKilnDetails={setKilnDetails} kilnDetails={kilnDetails} />
                </Box>
            }
            {(kilnDetails.kilnType === 'Conservation Burn - Hand Pile'
                || kilnDetails.kilnType === 'Conservation Burn - Machine Pile') &&
                <Box>
                    <Typography variant='h5'> Select the Correct Dimensions:</Typography>
                    <CbCard setKilnDetails={setKilnDetails} kilnDetails={kilnDetails} pileType={kilnDetails.kilnType.split(' - ')[1]} />
                </Box>
            }
            {kilnDetails.kilnType === 'Umptopia' &&
                <Box>
                    <Typography variant='h5'> Select the number of Square Panels:</Typography>
                    <UmptopiaCard setKilnDetails={setKilnDetails} kilnDetails={kilnDetails} variant={'Square'} isActive={activeUmptopiaKiln} setIsActive={setActiveUmptopiaKiln} />
                    <Typography variant='h5'> Select the number of Rectangular Panels:</Typography>
                    <UmptopiaCard setKilnDetails={setKilnDetails} kilnDetails={kilnDetails} variant={'Rectangle'} isActive={activeUmptopiaKiln} setIsActive={setActiveUmptopiaKiln} />
                    <Typography variant='h5'> Select the number of Polygon Sides Panels:</Typography>
                    <UmptopiaCard setKilnDetails={setKilnDetails} kilnDetails={kilnDetails} variant={'Polygon'} isActive={activeUmptopiaKiln} setIsActive={setActiveUmptopiaKiln} />
                </Box>
            }
            {formErrors.length > 0 &&
                <Alert sx={{ width: 'fit-content' }} severity='error'>
                    Ensure you have selected a card before submitting.
                </Alert>}
            {kilnDetails.kilnType &&
                <Button
                    variant='contained'
                    onClick={onSubmit}
                    sx={{ width: 'fit-content' }}
                >
                    Add to Kiln List
                </Button>}
            <Divider />
            <KilnList kilnType={kilnDetails.kilnType} />
        </Box>
    );
}
